import { CurrencyCodes } from 'common/configs/currency';
import { PaginationActionProps } from '../types';

// #region MollyWhitelabelCustomer
export enum MollyWhitelabelCustomerStatuses {
  PENDING = 'pending',
  ACCEPTED = 'accepted',
  REJECTED = 'rejected',
  SUSPENDED = 'suspended',
  CLOSED = 'closed',
}

export interface MollyWhitelabelCustomer {
  address: string;
  city: string;
  country: string;
  currency: string;
  date_of_birth: string;
  email: string;
  first_name: string;
  id: number;
  kyc_status: { address_verified: boolean; identity_verified: boolean };
  kyc_submitted: boolean;
  last_name: string;
  post_code: string;
  region: string;
  registration_date: string;
  preferences: {
    max_order: string;
    max_deposit_limit: string;
    self_excluded_until: string;
  };
  status: MollyWhitelabelCustomerStatuses;
  system_account_mappings: { [username: string]: number };
  telephone: string;
  telephone_verified_date: null | string;
  username: string;
}

interface MollyWhitelabelCustomerStarted {
  type: 'molly_whitelabel/customer/started';
}

interface MollyWhitelabelCustomerFinished {
  type: 'molly_whitelabel/customer/finished';
  customer?: MollyWhitelabelCustomer;
  error?: string;
}

export type MollyWhitelabelCustomerActions =
  | MollyWhitelabelCustomerStarted
  | MollyWhitelabelCustomerFinished;

export interface MollyWhitelabelCustomerSummary {
  contact_id: number;
  url: string;
  account_username: string;
  contact_username: string;
  status: string;
  country: string;
  first_name: string;
  last_name: string;
  preferences: Record<string, string>;
  kyc_submitted: boolean;
  kyc_status: {
    identity_verified: boolean;
    address_verified: boolean;
  };
  net_transfers: { [currency in CurrencyCodes]: string };
  net_totals: { [currency in CurrencyCodes]: string };
}

interface MollyWhitelabelCustomerSummariesStarted {
  type: 'molly_whitelabel/customers/started';
}

interface MollyWhitelabelCustomerSummariesFinished
  extends PaginationActionProps {
  type: 'molly_whitelabel/customers/finished';
  customers?: MollyWhitelabelCustomerSummary[];
  error?: string;
}

export type MollyWhitelabelCustomerSummariesActions =
  | MollyWhitelabelCustomerSummariesStarted
  | MollyWhitelabelCustomerSummariesFinished;
// #endregion

// #region MollyWhitelabelCustomerKYCDocument
export enum MollyWhitelabelCustomerKYCDocumentStatuses {
  SUBMITTED = 'submitted',
  ACCEPTED = 'accepted',
  REJECTED = 'rejected',
  EXPIRED = 'expired',
  FILED = 'filed',
}

export interface MollyWhitelabelCustomerKYCDocument {
  contact_username: string;
  description: string;
  expiry_date: string;
  id: number;
  issue_date: null | string;
  number: null | string;
  proof_of_address: boolean;
  proof_of_identity: boolean;
  status: MollyWhitelabelCustomerKYCDocumentStatuses;
  url: string;
}

interface MollyWhitelabelCustomerKYCDocumentsStarted {
  type: 'molly_whitelabel/customer/kyc/documents/started';
}

interface MollyWhitelabelCustomerKYCDocumentsFinished {
  type: 'molly_whitelabel/customer/kyc/documents/finished';
  documents?: MollyWhitelabelCustomerKYCDocument[];
  error?: string;
}

export type MollyWhitelabelCustomerKYCDocumentsActions =
  | MollyWhitelabelCustomerKYCDocumentsStarted
  | MollyWhitelabelCustomerKYCDocumentsFinished;

interface MollyWhitelabelKYCDocumentsStarted {
  type: 'molly_whitelabel/kyc/documents/started';
}

interface MollyWhitelabelKYCDocumentsFinished extends PaginationActionProps {
  type: 'molly_whitelabel/kyc/documents/finished';
  documents?: MollyWhitelabelCustomerKYCDocument[];
  error?: string;
}

export type MollyWhitelabelKYCDocumentsActions =
  | MollyWhitelabelKYCDocumentsStarted
  | MollyWhitelabelKYCDocumentsFinished;
// #endregion

// #region MollyWhitelabelCustomerLiveInfo
export interface MollyWhitelabelCustomerLiveInfo {
  balance: string;
  open_stakes: string;
}

interface MollyWhitelabelCustomerLiveInfoStarted {
  type: 'molly_whitelabel/customer/live_info/started';
}

interface MollyWhitelabelCustomerLiveInfoFinished {
  type: 'molly_whitelabel/customer/live_info/finished';
  liveInfo?: MollyWhitelabelCustomerLiveInfo;
  error?: string;
}

export type MollyWhitelabelCustomerLiveInfoActions =
  | MollyWhitelabelCustomerLiveInfoStarted
  | MollyWhitelabelCustomerLiveInfoFinished;
// #endregion

// #region MollyWhitelabelCustomerPL
export type MollyWhitelabelCustomerPL = { [currency in CurrencyCodes]: string };

interface MollyWhitelabelCustomerPLStarted {
  type: 'molly_whitelabel/customer/pl/started';
}

interface MollyWhitelabelCustomerPLFinished {
  type: 'molly_whitelabel/customer/pl/finished';
  pl?: MollyWhitelabelCustomerPL;
  error?: string;
}

export type MollyWhitelabelCustomerPLActions =
  | MollyWhitelabelCustomerPLStarted
  | MollyWhitelabelCustomerPLFinished;
// #endregion

// #region MollyWhitelabelCustomerNetTransfers
export type MollyWhitelabelCustomerNetTransfers = {
  [currency in CurrencyCodes]: string;
};

interface MollyWhitelabelCustomerNetTransfersStarted {
  type: 'molly_whitelabel/customer/net_transfers/started';
}

interface MollyWhitelabelCustomerNetTransfersFinished {
  type: 'molly_whitelabel/customer/net_transfers/finished';
  netTransfers?: MollyWhitelabelCustomerNetTransfers;
  error?: string;
}

export type MollyWhitelabelCustomerNetTransfersActions =
  | MollyWhitelabelCustomerNetTransfersStarted
  | MollyWhitelabelCustomerNetTransfersFinished;
// #endregion
